/**
 * Secret Management API Types
 *
 * Types for managing organization secrets (API keys, credentials, etc.)
 * for third-party systems like Valor Paytech.
 */

import { ValorThirdPartySystemId } from './types/ipg/valor/valor-system-constants';
import {
    ThirdPartySystemId,
    type ThirdPartySystemId as ThirdPartySystemIdType,
} from './types/third-party-system.enums';
import { GenericThirdPartySystemId } from './types/transaction-details.enums';
import { defaultIntegratedWebsiteDisplayNameBySystemId } from './backend_config.constants';
import {
    CredentialRotationAttemptState as DbCredentialRotationAttemptState,
    CredentialType,
    IpxCredentialDefinitionKind as DbIpxCredentialDefinitionKind,
} from '@pillar-portal/database/dist/enums';
import type { Role, Status } from '@pillar-portal/database/dist/enums';
import type { AuthorizableAction } from './authorization-contracts';
import type { OpaqueCursorPagination } from './authorization.types';
import type { UrlPatternString } from './backend_config.types';
import type { OpaqueCursorSearchRequest } from './omnisearch.types';
import type {
    IpxFlowFieldBindingSource,
    IpxFlowOutcomePredicate,
    IpxLoginWorkflow,
    IpxManualCaptureTriggerKind,
    IpxWebResourceModelDocument,
} from './samba-rpa-workflow.types';
import type {
    ValorCredentials,
    DraftValorCredentials,
} from './types/ipg/valor/valor-credential.types';
export type {
    ValorCredentialsPublic,
    ValorCredentialsSecret,
    ValorCredentialsPublicFields,
    ValorCredentialsSecretFields,
    ValorCredentialsAllFields,
    ValorRequestAuthFields,
} from './types/ipg/valor/valor-credential.types';

export interface GenericCredentialsPublic {
    username: string;
}
export interface GenericCredentialsSecret {
    password: string;
}
export type GenericCredentialsPublicFields = keyof GenericCredentialsPublic;
export type GenericCredentialsSecretFields = keyof GenericCredentialsSecret;

/**
 * Generic API credentials structure
 */
export interface GenericCredentials extends GenericCredentialsPublic, GenericCredentialsSecret {}

/**
 * Draft credential payload used while collecting incomplete credentials.
 */
export interface DraftGenericCredentials {
    username?: string;
    password?: string;
}

/**
 * Union type of all Generic credential field names (both public and secret)
 */
export type GenericCredentialsAllFields =
    | GenericCredentialsPublicFields
    | GenericCredentialsSecretFields;

export { CredentialType };

export const IpxCredentialDefinitionKind = DbIpxCredentialDefinitionKind;
export type IpxCredentialDefinitionKind =
    (typeof IpxCredentialDefinitionKind)[keyof typeof IpxCredentialDefinitionKind];

export const CredentialTypeDisplayName: Record<CredentialType, string> = {
    [CredentialType.PHYSICAL_TERMINAL]: 'Physical Terminal',
    [CredentialType.VIRTUAL_TERMINAL]: 'Virtual Terminal',
    [CredentialType.IPX]: 'Integrated website',
};

function listSupportedCredentialSystemIds(): readonly ThirdPartySystemIdType[] {
    return Object.values(ThirdPartySystemId);
}

export const SupportedCredentialSystemIds = listSupportedCredentialSystemIds();
export type SupportedCredentialSystemId = ThirdPartySystemIdType;

export interface ExplicitCredentialPayloadBySystem {
    [ValorThirdPartySystemId]: ValorCredentials;
}

export interface ExplicitCredentialDraftPayloadBySystem {
    [ValorThirdPartySystemId]: DraftValorCredentials;
}

export type CredentialsForSystem<T extends SupportedCredentialSystemId> =
    T extends keyof ExplicitCredentialPayloadBySystem
        ? ExplicitCredentialPayloadBySystem[T]
        : GenericCredentials;

export type DraftCredentialsForSystem<T extends SupportedCredentialSystemId> =
    T extends keyof ExplicitCredentialDraftPayloadBySystem
        ? ExplicitCredentialDraftPayloadBySystem[T]
        : DraftGenericCredentials;

export type ThirdPartyCredentialsBySystem = {
    readonly [T in SupportedCredentialSystemId]: CredentialsForSystem<T>;
};

export type ThirdPartyCredentialsDraftBySystem = {
    readonly [T in SupportedCredentialSystemId]: DraftCredentialsForSystem<T>;
};

export type ThirdPartyCredentials = ThirdPartyCredentialsBySystem[SupportedCredentialSystemId];
export type ThirdPartyCredentialsDraft =
    ThirdPartyCredentialsDraftBySystem[SupportedCredentialSystemId];

export const CredentialOwnerScope = {
    ROLE_ASSIGNMENT: 'role_assignment',
    ORGANIZATION: 'organization',
} as const;
export type CredentialOwnerScope = (typeof CredentialOwnerScope)[keyof typeof CredentialOwnerScope];

export const UNIQUE_CREDENTIAL_DEFINITION_KEY = '';

export type CredentialTypeDefinitionKind = 'unique' | 'multi';
export interface AtomicCredentialTypeDefinition {
    name: string; // Human-readable name
    publicFields: string[]; // Fields stored in cleartext for search (in public_fields JSONB)
    secretFields: string[]; // Fields stored encrypted (in encrypted_credentials)
    requiredFields: string[]; // All required fields
    credentialOwnerScope: CredentialOwnerScope;
}

export interface UniqueCredentialTypeDefinition {
    kind: 'unique';
    fields: AtomicCredentialTypeDefinition;
}
export interface MultiCredentialTypeDefinition {
    kind: 'multi';
    definitions: Record<string, AtomicCredentialTypeDefinition>;
}

/** type-definition type: the Zod schema for this discriminated union should default to UniqueCredentialTypeDefinition */
export type CredentialTypeDefinition =
    | UniqueCredentialTypeDefinition
    | MultiCredentialTypeDefinition;

export interface IpxCredentialDefinitionActivePolicyMetadata {
    organization_ipx_credential_policy_uuid: string;
    status: Status;
}

export interface IpxCredentialRuntimeWebResourceModelProjection {
    ipx_web_resource_model_uuid: string;
    web_resource_model: IpxWebResourceModelDocument;
    model_digest: string;
}

export interface IpxCredentialDefinitionBaseProjection {
    ipx_credential_definition_uuid: string;
    system_id: SupportedCredentialSystemId;
    credential_definition_key: string;
    credential_definition_kind: IpxCredentialDefinitionKind;
    status: Status;
    credential_owner_scope: CredentialOwnerScope;
    credential_field_config: IpxCredentialFieldConfigMap;
    active_policy: IpxCredentialDefinitionActivePolicyMetadata | null;
}

export interface IpxWebLoginCredentialDefinitionProjection
    extends IpxCredentialDefinitionBaseProjection {
    credential_definition_kind:
        typeof IpxCredentialDefinitionKind.WEB_LOGIN_WORKFLOW;
    trigger_url_pattern: UrlPatternString[];
    login_workflow: IpxLoginWorkflow;
    ipx_web_resource_model?: IpxCredentialRuntimeWebResourceModelProjection | null;
}

export interface IpxMachineAuthCredentialDefinitionProjection
    extends IpxCredentialDefinitionBaseProjection {
    credential_definition_kind: typeof IpxCredentialDefinitionKind.MACHINE_AUTH;
    trigger_url_pattern: null;
    login_workflow: null;
    ipx_web_resource_model?: null;
}

export type IpxCredentialDefinitionProjection =
    | IpxWebLoginCredentialDefinitionProjection
    | IpxMachineAuthCredentialDefinitionProjection;

export interface IpxCredentialDefinitionSearchRequest
    extends OpaqueCursorSearchRequest {
    status?: Status;
    ipx_system_id?: SupportedCredentialSystemId;
}

export interface IpxCredentialDefinitionSearchBaseListItem {
    ipx_credential_definition_uuid: string;
    name: string;
    system_id: SupportedCredentialSystemId;
    credential_definition_key: string;
    credential_definition_kind: IpxCredentialDefinitionKind;
    status: Status;
    credential_owner_scope: CredentialOwnerScope;
    credential_field_config: IpxCredentialFieldConfigMap;
    credential_type_display_name: string;
    public_field_names: string[];
    secret_field_names: string[];
    required_field_names: string[];
}

export interface IpxWebLoginCredentialDefinitionSearchListItem
    extends IpxCredentialDefinitionSearchBaseListItem {
    credential_definition_kind:
        typeof IpxCredentialDefinitionKind.WEB_LOGIN_WORKFLOW;
    trigger_url_pattern: UrlPatternString[];
    login_workflow: IpxLoginWorkflow;
    ipx_web_resource_model_uuid?: string | null;
}

export interface IpxMachineAuthCredentialDefinitionSearchListItem
    extends IpxCredentialDefinitionSearchBaseListItem {
    credential_definition_kind: typeof IpxCredentialDefinitionKind.MACHINE_AUTH;
    trigger_url_pattern: null;
    login_workflow: null;
    ipx_web_resource_model_uuid?: null;
}

export type IpxCredentialDefinitionSearchListItem =
    | IpxWebLoginCredentialDefinitionSearchListItem
    | IpxMachineAuthCredentialDefinitionSearchListItem;

export interface IpxCredentialDefinitionSearchResponse {
    definitions: IpxCredentialDefinitionSearchListItem[];
    pagination: OpaqueCursorPagination;
}

export interface SearchOrganizationIpxCredentialPolicyDefinitionsRequest
    extends IpxCredentialDefinitionSearchRequest {
    organization_uuid: string;
}

export interface SearchOrganizationIpxCredentialPolicyDefinitionsResponse {
    definitions: IpxWebLoginCredentialDefinitionSearchListItem[];
    pagination: OpaqueCursorPagination;
}

export const CredentialRotationAttemptState = DbCredentialRotationAttemptState;
export type CredentialRotationAttemptState =
    (typeof CredentialRotationAttemptState)[keyof typeof CredentialRotationAttemptState];

export const CredentialRotationEvidenceKind = {
    DOM_SUCCESS: 'dom_success',
    DOM_FAILURE: 'dom_failure',
    SUBMISSION_TRIGGER: 'submission_trigger',
    INJECTION: 'injection',
    MANUAL_PROMOTION: 'manual_promotion',
} as const;
export type CredentialRotationEvidenceKind =
    (typeof CredentialRotationEvidenceKind)[keyof typeof CredentialRotationEvidenceKind];

export interface CredentialRotationDomSuccessEvidence {
    kind: typeof CredentialRotationEvidenceKind.DOM_SUCCESS;
    flow_key: string;
    screen_key: string;
    predicate: IpxFlowOutcomePredicate;
    observed_ts: string;
}

export interface CredentialRotationDomFailureEvidence {
    kind: typeof CredentialRotationEvidenceKind.DOM_FAILURE;
    flow_key: string;
    screen_key: string;
    predicate: IpxFlowOutcomePredicate;
    observed_ts: string;
}

export interface CredentialRotationSubmissionTriggerEvidence {
    kind: typeof CredentialRotationEvidenceKind.SUBMISSION_TRIGGER;
    flow_key: string;
    screen_key: string;
    trigger_kind: IpxManualCaptureTriggerKind;
    observed_ts: string;
    request_url?: string;
}

export interface CredentialRotationInjectionEvidence {
    kind: typeof CredentialRotationEvidenceKind.INJECTION;
    flow_key: string;
    screen_key: string;
    field_names: IpxCredentialFieldName[];
    observed_ts: string;
}

export interface CredentialRotationManualPromotionEvidence {
    kind: typeof CredentialRotationEvidenceKind.MANUAL_PROMOTION;
    actor_role_assignment_uuid: string;
    confirmed_ts: string;
    note?: string;
}

export type CredentialRotationEvidence =
    | CredentialRotationDomSuccessEvidence
    | CredentialRotationDomFailureEvidence
    | CredentialRotationSubmissionTriggerEvidence
    | CredentialRotationInjectionEvidence
    | CredentialRotationManualPromotionEvidence;

export interface CredentialRotationGeneratedFieldMetadata {
    field_name: IpxCredentialFieldName;
    length: number;
    required_classes: IpxCredentialFieldCharacterClass[];
}

export interface CredentialRotationGenerationMetadata {
    policy_version: number;
    field_names: IpxCredentialFieldName[];
    fields: Record<
        IpxCredentialFieldName,
        CredentialRotationGeneratedFieldMetadata
    >;
    generated_ts: string;
}

export interface CredentialRotationAttemptSummary {
    credential_rotation_attempt_uuid: string;
    credential_uuid: string;
    ipx_credential_definition_uuid: string;
    organization_uuid: string;
    role_assignment_uuid: string | null;
    actor_role_assignment_uuid: string | null;
    state: CredentialRotationAttemptState;
    flow_key: string;
    target_field_names: IpxCredentialFieldName[];
    created_ts: string;
    expires_ts: string;
    injected_ts: string | null;
    submitted_ts: string | null;
    promoted_ts: string | null;
    failed_ts: string | null;
    evidence_kind: CredentialRotationEvidenceKind | null;
}

export interface CredentialRotationBeginRequest {
    ipx_credential_definition_uuid: string;
    credential_uuid: string;
    model_digest: string;
    flow_key: string;
    screen_key: string;
}

export interface CredentialRotationBeginFieldBindingValue {
    credential_field_name: IpxCredentialFieldName;
    source: IpxFlowFieldBindingSource;
    screen_key: string;
    element_key: string;
    field_value: string;
}

export const CredentialRotationBeginResult = {
    RESOLVED: 'resolved',
    ACTIVE_RECOVERY_REQUIRED: 'active_recovery_required',
    MISSING_CREDENTIAL: 'missing_credential',
    NOT_ROTATABLE: 'not_rotatable',
    STALE_PROJECTION: 'stale_projection',
    UNAUTHORIZED: 'unauthorized',
} as const;
export type CredentialRotationBeginResult =
    (typeof CredentialRotationBeginResult)[keyof typeof CredentialRotationBeginResult];

export interface CredentialRotationBeginResolvedResponse {
    result: typeof CredentialRotationBeginResult.RESOLVED;
    credential_rotation_attempt_uuid: string;
    state: CredentialRotationAttemptState;
    expires_ts: string;
    field_names: IpxCredentialFieldName[];
    field_binding_values: CredentialRotationBeginFieldBindingValue[];
}

export interface CredentialRotationBeginActiveRecoveryRequiredResponse {
    result: typeof CredentialRotationBeginResult.ACTIVE_RECOVERY_REQUIRED;
    active_attempt: CredentialRotationAttemptSummary;
}

export interface CredentialRotationBeginMissingCredentialResponse {
    result: typeof CredentialRotationBeginResult.MISSING_CREDENTIAL;
}

export interface CredentialRotationBeginNotRotatableResponse {
    result: typeof CredentialRotationBeginResult.NOT_ROTATABLE;
    reason: string;
}

export interface CredentialRotationBeginStaleProjectionResponse {
    result: typeof CredentialRotationBeginResult.STALE_PROJECTION;
    current_model_digest: string;
}

export interface CredentialRotationBeginUnauthorizedResponse {
    result: typeof CredentialRotationBeginResult.UNAUTHORIZED;
}

export type CredentialRotationBeginResponse =
    | CredentialRotationBeginResolvedResponse
    | CredentialRotationBeginActiveRecoveryRequiredResponse
    | CredentialRotationBeginMissingCredentialResponse
    | CredentialRotationBeginNotRotatableResponse
    | CredentialRotationBeginStaleProjectionResponse
    | CredentialRotationBeginUnauthorizedResponse;

export interface CredentialRotationInjectedRequest {
    credential_rotation_attempt_uuid: string;
    model_digest: string;
    screen_key: string;
    field_names: IpxCredentialFieldName[];
    injection_evidence: CredentialRotationInjectionEvidence;
}

export interface CredentialRotationSubmittedRequest {
    credential_rotation_attempt_uuid: string;
    model_digest: string;
    flow_key: string;
    screen_key: string;
    submission_evidence: CredentialRotationSubmissionTriggerEvidence;
}

export interface CredentialRotationPromoteRequest {
    credential_rotation_attempt_uuid: string;
    model_digest: string;
    flow_key: string;
    success_evidence: CredentialRotationDomSuccessEvidence;
}

export const CredentialRotationPromoteResult = {
    PROMOTED: 'promoted',
    INVALID_STATE: 'invalid_state',
    EXPIRED_ATTEMPT: 'expired_attempt',
    STALE_PROJECTION: 'stale_projection',
    UNAUTHORIZED: 'unauthorized',
    PROMOTION_FAILED: 'promotion_failed',
} as const;
export type CredentialRotationPromoteResult =
    (typeof CredentialRotationPromoteResult)[keyof typeof CredentialRotationPromoteResult];

export interface CredentialRotationPromoteSucceededResponse {
    result: typeof CredentialRotationPromoteResult.PROMOTED;
    attempt: CredentialRotationAttemptSummary;
}

export interface CredentialRotationPromoteInvalidStateResponse {
    result: typeof CredentialRotationPromoteResult.INVALID_STATE;
    current_state: CredentialRotationAttemptState;
}

export interface CredentialRotationPromoteExpiredAttemptResponse {
    result: typeof CredentialRotationPromoteResult.EXPIRED_ATTEMPT;
    expires_ts: string;
}

export interface CredentialRotationPromoteStaleProjectionResponse {
    result: typeof CredentialRotationPromoteResult.STALE_PROJECTION;
    current_model_digest: string;
}

export interface CredentialRotationPromoteUnauthorizedResponse {
    result: typeof CredentialRotationPromoteResult.UNAUTHORIZED;
}

export interface CredentialRotationPromotePromotionFailedResponse {
    result: typeof CredentialRotationPromoteResult.PROMOTION_FAILED;
    attempt: CredentialRotationAttemptSummary;
    reason: string;
}

export type CredentialRotationPromoteResponse =
    | CredentialRotationPromoteSucceededResponse
    | CredentialRotationPromoteInvalidStateResponse
    | CredentialRotationPromoteExpiredAttemptResponse
    | CredentialRotationPromoteStaleProjectionResponse
    | CredentialRotationPromoteUnauthorizedResponse
    | CredentialRotationPromotePromotionFailedResponse;

export interface CredentialRotationFailRequest {
    credential_rotation_attempt_uuid: string;
    model_digest: string;
    flow_key: string;
    failure_evidence: CredentialRotationDomFailureEvidence;
}

export interface ManualCredentialCaptureRequest {
    ipx_credential_definition_uuid: string;
    credential_uuid?: string;
    model_digest: string;
    login_flow_key: string;
    captured_field_patch: Record<IpxCredentialFieldName, string>;
    success_evidence: CredentialRotationDomSuccessEvidence;
}

export const ManualCredentialCaptureResult = {
    CREATED: 'created',
    UPDATED: 'updated',
    UNAUTHORIZED: 'unauthorized',
    STALE_PROJECTION: 'stale_projection',
    INVALID_EVIDENCE: 'invalid_evidence',
    PERSISTENCE_FAILED: 'persistence_failed',
} as const;
export type ManualCredentialCaptureResult =
    (typeof ManualCredentialCaptureResult)[keyof typeof ManualCredentialCaptureResult];

export interface ManualCredentialCaptureCreatedResponse {
    result: typeof ManualCredentialCaptureResult.CREATED;
    credential_uuid: string;
    ipx_credential_definition_uuid: string;
    account_id: string;
    masked_preview: string;
    updated_field_names: IpxCredentialFieldName[];
}

export interface ManualCredentialCaptureUpdatedResponse {
    result: typeof ManualCredentialCaptureResult.UPDATED;
    credential_uuid: string;
    ipx_credential_definition_uuid: string;
    account_id: string;
    masked_preview: string;
    updated_field_names: IpxCredentialFieldName[];
}

export interface ManualCredentialCaptureUnauthorizedResponse {
    result: typeof ManualCredentialCaptureResult.UNAUTHORIZED;
}

export interface ManualCredentialCaptureStaleProjectionResponse {
    result: typeof ManualCredentialCaptureResult.STALE_PROJECTION;
    current_model_digest: string;
}

export interface ManualCredentialCaptureInvalidEvidenceResponse {
    result: typeof ManualCredentialCaptureResult.INVALID_EVIDENCE;
    reason: string;
}

export interface ManualCredentialCapturePersistenceFailedResponse {
    result: typeof ManualCredentialCaptureResult.PERSISTENCE_FAILED;
    reason: string;
}

export type ManualCredentialCaptureResponse =
    | ManualCredentialCaptureCreatedResponse
    | ManualCredentialCaptureUpdatedResponse
    | ManualCredentialCaptureUnauthorizedResponse
    | ManualCredentialCaptureStaleProjectionResponse
    | ManualCredentialCaptureInvalidEvidenceResponse
    | ManualCredentialCapturePersistenceFailedResponse;

export interface CredentialRotationHistoryRequest {
    credential_uuid: string;
    limit?: number;
    cursor?: string;
    state?: CredentialRotationAttemptState;
}

export interface CredentialRotationHistoryCapabilities {
    can_view_history: boolean;
}

export interface CredentialRotationHistoryAttemptCapabilities {
    can_manual_promote: boolean;
    manual_promote_disabled_reason?: string;
}

export interface CredentialRotationHistoryTargetFieldDisplay {
    field_name: IpxCredentialFieldName;
    label: string;
}

export interface CredentialRotationHistoryDisplayMetadata {
    ipx_credential_definition_name: string;
    account_label: string;
    target_fields: CredentialRotationHistoryTargetFieldDisplay[];
    flow_display_name: string | null;
    actor_role_assignment_label: string | null;
}

export interface CredentialRotationHistoryListItem {
    attempt: CredentialRotationAttemptSummary;
    display: CredentialRotationHistoryDisplayMetadata;
    capabilities: CredentialRotationHistoryAttemptCapabilities;
}

export interface CredentialRotationHistoryResponse {
    attempts: CredentialRotationHistoryListItem[];
    pagination: OpaqueCursorPagination;
    capabilities: CredentialRotationHistoryCapabilities;
}

export interface CredentialRotationManualPromotionConfirmation {
    confirmed: true;
    note?: string;
}

export interface CredentialRotationManualPromotionRequest {
    credential_rotation_attempt_uuid: string;
    confirmation: CredentialRotationManualPromotionConfirmation;
}

export const CredentialRotationManualPromotionResult = {
    PROMOTED: 'promoted',
    UNAUTHORIZED: 'unauthorized',
    INVALID_STATE: 'invalid_state',
    EXPIRED_ATTEMPT: 'expired_attempt',
    STALE_TARGET: 'stale_target',
    PROMOTION_FAILED: 'promotion_failed',
} as const;
export type CredentialRotationManualPromotionResult =
    (typeof CredentialRotationManualPromotionResult)[keyof typeof CredentialRotationManualPromotionResult];

export interface CredentialRotationManualPromotionSucceededResponse {
    result: typeof CredentialRotationManualPromotionResult.PROMOTED;
    attempt: CredentialRotationAttemptSummary;
}

export interface CredentialRotationManualPromotionUnauthorizedResponse {
    result: typeof CredentialRotationManualPromotionResult.UNAUTHORIZED;
}

export interface CredentialRotationManualPromotionInvalidStateResponse {
    result: typeof CredentialRotationManualPromotionResult.INVALID_STATE;
    current_state: CredentialRotationAttemptState;
}

export interface CredentialRotationManualPromotionExpiredAttemptResponse {
    result: typeof CredentialRotationManualPromotionResult.EXPIRED_ATTEMPT;
    expires_ts: string;
}

export interface CredentialRotationManualPromotionStaleTargetResponse {
    result: typeof CredentialRotationManualPromotionResult.STALE_TARGET;
    reason: string;
}

export interface CredentialRotationManualPromotionPromotionFailedResponse {
    result: typeof CredentialRotationManualPromotionResult.PROMOTION_FAILED;
    attempt: CredentialRotationAttemptSummary;
    reason: string;
}

export type CredentialRotationManualPromotionResponse =
    | CredentialRotationManualPromotionSucceededResponse
    | CredentialRotationManualPromotionUnauthorizedResponse
    | CredentialRotationManualPromotionInvalidStateResponse
    | CredentialRotationManualPromotionExpiredAttemptResponse
    | CredentialRotationManualPromotionStaleTargetResponse
    | CredentialRotationManualPromotionPromotionFailedResponse;

export const CredentialRotationDisabledReason = {
    NO_EFFECTIVE_CREDENTIAL: 'no_effective_credential',
    MISSING_USE_PERMISSION: 'missing_use_permission',
    MISSING_ROTATE_PERMISSION: 'missing_rotate_permission',
    MISSING_AUDIT_PERMISSION: 'missing_audit_permission',
    NOT_ADMIN_FOR_ORGANIZATION: 'not_admin_for_organization',
    NO_ROTATION_FLOW: 'no_rotation_flow',
    NO_ROTATABLE_SECRET_FIELDS: 'no_rotatable_secret_fields',
    ACTIVE_ATTEMPT_EXISTS: 'active_attempt_exists',
    INVALID_GENERATION_POLICY: 'invalid_generation_policy',
    APP_RUNTIME_REQUIRED: 'app_runtime_required',
} as const;
export type CredentialRotationDisabledReason =
    (typeof CredentialRotationDisabledReason)[keyof typeof CredentialRotationDisabledReason];

export const CredentialRotationRecoverySeverity = {
    INFO: 'info',
    WARNING: 'warning',
    ERROR: 'error',
} as const;
export type CredentialRotationRecoverySeverity =
    (typeof CredentialRotationRecoverySeverity)[keyof typeof CredentialRotationRecoverySeverity];

export interface PortalCredentialRotationFlowSummary {
    flow_key: string;
    display_name: string;
    target_field_names: IpxCredentialFieldName[];
}

export interface PortalCredentialRotationRecoverySummary {
    severity: CredentialRotationRecoverySeverity;
    message: string;
}

export interface PortalCredentialRotationEligibility {
    ipx_credential_definition_uuid: string;
    credential_uuid: string | null;
    flow_summaries: PortalCredentialRotationFlowSummary[];
    rotatable_field_descriptors: IpxCredentialFieldDescriptor[];
    active_attempt: CredentialRotationAttemptSummary | null;
    can_rotate: boolean;
    rotate_disabled_reason: CredentialRotationDisabledReason | null;
    can_view_history: boolean;
    history_disabled_reason: CredentialRotationDisabledReason | null;
    recovery_summary: PortalCredentialRotationRecoverySummary | null;
}

export interface OrganizationIpxCredentialPolicyCollectionCapabilities {
    can_create: boolean;
}

export interface OrganizationIpxCredentialPolicyCapabilities {
    can_view: boolean;
    can_disable: boolean;
    can_enable: boolean;
    can_delete: boolean;
    can_restore: boolean;
}

export interface OrganizationIpxCredentialPolicyListItem {
    organization_ipx_credential_policy_uuid: string;
    organization_uuid: string;
    ipx_credential_definition_uuid: string;
    ipx_credential_definition_name: string;
    default_account_id: string;
    system_id: SupportedCredentialSystemId;
    credential_definition_key: string;
    status: Status;
    credential_count: number;
    missing_personal_credential_count: number;
    capabilities: OrganizationIpxCredentialPolicyCapabilities;
}

export interface ListOrganizationIpxCredentialPoliciesRequest {
    organization_uuid: string;
    status?: Status;
    limit?: number;
    cursor?: string;
}

export interface ListOrganizationIpxCredentialPoliciesResponse {
    capabilities: OrganizationIpxCredentialPolicyCollectionCapabilities;
    policies: OrganizationIpxCredentialPolicyListItem[];
    pagination?: OpaqueCursorPagination;
}

export interface DashboardWebsiteCredentialPolicyListItem {
    organization_ipx_credential_policy_uuid: string;
    organization_uuid: string;
    ipx_credential_definition_uuid: string;
    ipx_credential_definition_name: string;
    default_account_id: string;
    system_id: SupportedCredentialSystemId;
    credential_definition_key: string;
    status: Status;
}

export interface DashboardWebsiteCredentialSlot {
    policy: DashboardWebsiteCredentialPolicyListItem;
    field_descriptors: IpxCredentialFieldDescriptor[];
    rotation_eligibility: PortalCredentialRotationEligibility | null;
    default_account_id: string;
    credentials: IpxCredentialSummaryListItem[];
    can_create: boolean;
}

export interface ListDashboardWebsiteCredentialsRequest {
    organization_uuid: string;
}

export interface ListDashboardWebsiteCredentialsResponse {
    credential_slots: DashboardWebsiteCredentialSlot[];
}

export interface IpxCredentialDefaultAccountIdSource {
    system_id: SupportedCredentialSystemId;
    ipx_credential_definition_name: string;
    trigger_url_patterns: readonly UrlPatternString[];
}

export function getDefaultIpxCredentialAccountId(
    source: IpxCredentialDefaultAccountIdSource
): string {
    if (source.system_id === GenericThirdPartySystemId) {
        const genericWebsiteUrl = source.trigger_url_patterns
            .map((pattern) => pattern.trim())
            .find((pattern) => pattern.length > 0);
        if (genericWebsiteUrl === undefined) {
            throw new Error(
                'Generic IPX credential definitions require a trigger URL pattern'
            );
        }
        return genericWebsiteUrl;
    }

    const configName =
        defaultIntegratedWebsiteDisplayNameBySystemId[source.system_id];
    if (configName === undefined || configName.trim().length === 0) {
        throw new Error(
            `No default account id is configured for IPX system ${source.system_id}`
        );
    }
    return configName;
}

export interface CreateOrganizationIpxCredentialPolicyRequest {
    organization_uuid: string;
    ipx_credential_definition_uuid: string;
}

export interface OrganizationIpxCredentialPolicyLifecycleRequest {
    organization_uuid: string;
    organization_ipx_credential_policy_uuid: string;
}

export interface OrganizationIpxCredentialPolicyResponse {
    policy: OrganizationIpxCredentialPolicyListItem;
}

export const IpxCredentialScopeType = {
    ORGANIZATION: 'organization',
    ROLE_ASSIGNMENT: 'role_assignment',
} as const;
export type IpxCredentialScopeType =
    (typeof IpxCredentialScopeType)[keyof typeof IpxCredentialScopeType];

export interface OrganizationIpxCredentialScope {
    scope_type: typeof IpxCredentialScopeType.ORGANIZATION;
}

export interface RoleAssignmentIpxCredentialScope {
    scope_type: typeof IpxCredentialScopeType.ROLE_ASSIGNMENT;
    role_assignment_uuid: string;
}

export type IpxCredentialScope =
    | OrganizationIpxCredentialScope
    | RoleAssignmentIpxCredentialScope;

export const IpxCredentialFieldKind = {
    TEXT: 'text',
} as const;
export type IpxCredentialFieldKind =
    (typeof IpxCredentialFieldKind)[keyof typeof IpxCredentialFieldKind];

export const IpxCredentialFieldClassification = {
    PUBLIC: 'public',
    SECRET: 'secret',
} as const;
export type IpxCredentialFieldClassification =
    (typeof IpxCredentialFieldClassification)[keyof typeof IpxCredentialFieldClassification];

export type IpxCredentialFieldName = string;

export const IpxCredentialFieldNamePattern: RegExp =
    /^[A-Za-z0-9_:!@#$%^&*() -]+$/;

export const IpxCredentialFieldCharacterClass = {
    LOWERCASE: 'lowercase',
    UPPERCASE: 'uppercase',
    DIGIT: 'digit',
    SYMBOL: 'symbol',
} as const;
export type IpxCredentialFieldCharacterClass =
    (typeof IpxCredentialFieldCharacterClass)[keyof typeof IpxCredentialFieldCharacterClass];

export interface IpxCredentialFieldValidationPolicy {
    min_length?: number;
    max_length?: number;
    pattern?: string;
    allowed_characters?: string;
    forbidden_characters?: string;
}

export interface IpxCredentialFieldGenerationPolicy {
    min_length?: number;
    max_length?: number;
    allowed_characters?: string;
    required_classes?: IpxCredentialFieldCharacterClass[];
    forbidden_characters?: string;
}

export interface IpxCredentialFieldDisplayConfig {
    label: string;
}
export type IpxCredentialFieldDisplayMetadata = IpxCredentialFieldDisplayConfig;

export interface IpxCredentialFieldConfig {
    field_name: IpxCredentialFieldName;
    field_kind: IpxCredentialFieldKind;
    field_classification: IpxCredentialFieldClassification;
    required: boolean;
    display: IpxCredentialFieldDisplayConfig;
    validation: IpxCredentialFieldValidationPolicy;
    generation: IpxCredentialFieldGenerationPolicy;
    rotatable: boolean;
    confirmation_field_name?: IpxCredentialFieldName;
}

export type IpxCredentialFieldConfigMap = Record<
    IpxCredentialFieldName,
    IpxCredentialFieldConfig
>;

export const IpxCredentialFieldConfigValidationIssueCode = {
    PUBLIC_FIELD_ROTATABLE: 'public_field_rotatable',
    ROTATABLE_FIELD_MISSING_GENERATION_LENGTH:
        'rotatable_field_missing_generation_length',
    GENERATION_MIN_LENGTH_BELOW_VALIDATION_MIN:
        'generation_min_length_below_validation_min',
    GENERATION_MAX_LENGTH_ABOVE_VALIDATION_MAX:
        'generation_max_length_above_validation_max',
    REQUIRED_CLASS_UNAVAILABLE: 'required_class_unavailable',
    EMPTY_GENERATION_ALPHABET: 'empty_generation_alphabet',
    CONFIRMATION_FIELD_STORED: 'confirmation_field_stored',
} as const;
export type IpxCredentialFieldConfigValidationIssueCode =
    (typeof IpxCredentialFieldConfigValidationIssueCode)[keyof typeof IpxCredentialFieldConfigValidationIssueCode];

export interface IpxCredentialFieldConfigValidationIssue {
    code: IpxCredentialFieldConfigValidationIssueCode;
    field_name: IpxCredentialFieldName;
    message: string;
    path: string[];
}

export interface IpxCredentialFieldConfigValidationResult {
    valid: boolean;
    issues: IpxCredentialFieldConfigValidationIssue[];
}

const CredentialGenerationCharacterClassAlphabet: Record<
    IpxCredentialFieldCharacterClass,
    string
> = {
    [IpxCredentialFieldCharacterClass.LOWERCASE]: 'abcdefghijklmnopqrstuvwxyz',
    [IpxCredentialFieldCharacterClass.UPPERCASE]: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    [IpxCredentialFieldCharacterClass.DIGIT]: '0123456789',
    [IpxCredentialFieldCharacterClass.SYMBOL]:
        '!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~',
};

const DefaultCredentialGenerationAlphabet = [
    CredentialGenerationCharacterClassAlphabet[
        IpxCredentialFieldCharacterClass.LOWERCASE
    ],
    CredentialGenerationCharacterClassAlphabet[
        IpxCredentialFieldCharacterClass.UPPERCASE
    ],
    CredentialGenerationCharacterClassAlphabet[IpxCredentialFieldCharacterClass.DIGIT],
    CredentialGenerationCharacterClassAlphabet[IpxCredentialFieldCharacterClass.SYMBOL],
].join('');

function removeForbiddenCharacters(
    alphabet: string,
    forbiddenCharacters: string | undefined
): string {
    if (forbiddenCharacters === undefined) {
        return alphabet;
    }
    const forbiddenCharacterSet = new Set(forbiddenCharacters.split(''));
    return alphabet
        .split('')
        .filter((character) => !forbiddenCharacterSet.has(character))
        .join('');
}

function alphabetContainsAnyClassCharacter(
    alphabet: string,
    requiredClass: IpxCredentialFieldCharacterClass
): boolean {
    const classAlphabet =
        CredentialGenerationCharacterClassAlphabet[requiredClass];
    return classAlphabet
        .split('')
        .some((character) => alphabet.includes(character));
}

function addFieldConfigValidationIssue(
    issues: IpxCredentialFieldConfigValidationIssue[],
    issue: IpxCredentialFieldConfigValidationIssue
): void {
    issues.push(issue);
}

export function validateIpxCredentialFieldConfigMap(
    fieldConfigMap: IpxCredentialFieldConfigMap
): IpxCredentialFieldConfigValidationResult {
    const issues: IpxCredentialFieldConfigValidationIssue[] = [];
    const fieldNames = new Set(Object.keys(fieldConfigMap));

    for (const fieldName of Object.keys(fieldConfigMap)) {
        const fieldConfig = fieldConfigMap[fieldName];
        if (fieldConfig === undefined) {
            continue;
        }

        if (
            fieldConfig.rotatable &&
            fieldConfig.field_classification ===
                IpxCredentialFieldClassification.PUBLIC
        ) {
            addFieldConfigValidationIssue(issues, {
                code: IpxCredentialFieldConfigValidationIssueCode.PUBLIC_FIELD_ROTATABLE,
                field_name: fieldName,
                message: 'public credential fields cannot be rotatable',
                path: [fieldName, 'rotatable'],
            });
        }

        if (
            fieldConfig.rotatable &&
            (fieldConfig.generation.min_length === undefined ||
                fieldConfig.generation.max_length === undefined)
        ) {
            addFieldConfigValidationIssue(issues, {
                code: IpxCredentialFieldConfigValidationIssueCode.ROTATABLE_FIELD_MISSING_GENERATION_LENGTH,
                field_name: fieldName,
                message:
                    'rotatable credential fields require generation length bounds',
                path: [fieldName, 'generation'],
            });
        }

        if (
            fieldConfig.generation.min_length !== undefined &&
            fieldConfig.validation.min_length !== undefined &&
            fieldConfig.generation.min_length < fieldConfig.validation.min_length
        ) {
            addFieldConfigValidationIssue(issues, {
                code: IpxCredentialFieldConfigValidationIssueCode.GENERATION_MIN_LENGTH_BELOW_VALIDATION_MIN,
                field_name: fieldName,
                message:
                    'generation min_length must satisfy validation min_length',
                path: [fieldName, 'generation', 'min_length'],
            });
        }

        if (
            fieldConfig.generation.max_length !== undefined &&
            fieldConfig.validation.max_length !== undefined &&
            fieldConfig.generation.max_length > fieldConfig.validation.max_length
        ) {
            addFieldConfigValidationIssue(issues, {
                code: IpxCredentialFieldConfigValidationIssueCode.GENERATION_MAX_LENGTH_ABOVE_VALIDATION_MAX,
                field_name: fieldName,
                message:
                    'generation max_length must satisfy validation max_length',
                path: [fieldName, 'generation', 'max_length'],
            });
        }

        const effectiveGenerationAlphabet = removeForbiddenCharacters(
            fieldConfig.generation.allowed_characters ??
                DefaultCredentialGenerationAlphabet,
            fieldConfig.generation.forbidden_characters
        );
        if (
            fieldConfig.rotatable &&
            effectiveGenerationAlphabet.length === 0
        ) {
            addFieldConfigValidationIssue(issues, {
                code: IpxCredentialFieldConfigValidationIssueCode.EMPTY_GENERATION_ALPHABET,
                field_name: fieldName,
                message:
                    'rotatable credential fields require a non-empty generation alphabet',
                path: [fieldName, 'generation', 'allowed_characters'],
            });
        }

        for (const requiredClass of fieldConfig.generation.required_classes ?? []) {
            if (
                !alphabetContainsAnyClassCharacter(
                    effectiveGenerationAlphabet,
                    requiredClass
                )
            ) {
                addFieldConfigValidationIssue(issues, {
                    code: IpxCredentialFieldConfigValidationIssueCode.REQUIRED_CLASS_UNAVAILABLE,
                    field_name: fieldName,
                    message:
                        'generation alphabet does not contain the required character class',
                    path: [fieldName, 'generation', 'required_classes'],
                });
            }
        }

        if (
            fieldConfig.confirmation_field_name !== undefined &&
            fieldNames.has(fieldConfig.confirmation_field_name)
        ) {
            addFieldConfigValidationIssue(issues, {
                code: IpxCredentialFieldConfigValidationIssueCode.CONFIRMATION_FIELD_STORED,
                field_name: fieldName,
                message:
                    'confirmation field names must not be stored credential fields',
                path: [fieldName, 'confirmation_field_name'],
            });
        }
    }

    return {
        valid: issues.length === 0,
        issues,
    };
}

function buildDefaultFieldConfig(
    fieldName: string,
    fieldClassification: IpxCredentialFieldClassification,
    required: boolean
): IpxCredentialFieldConfig {
    return {
        field_name: fieldName,
        field_kind: IpxCredentialFieldKind.TEXT,
        field_classification: fieldClassification,
        required,
        display: {
            label: toCredentialFieldDisplayLabel(fieldName),
        },
        validation: {},
        generation: {},
        rotatable: false,
    };
}

export function buildDefaultFieldConfigForDefinition(
    atomicDefinition: AtomicCredentialTypeDefinition
): IpxCredentialFieldConfigMap {
    const requiredFieldNames = new Set(atomicDefinition.requiredFields);
    const fieldConfigMap: IpxCredentialFieldConfigMap = {};
    const emittedFieldNames = new Set<string>();

    for (const fieldName of atomicDefinition.publicFields) {
        if (emittedFieldNames.has(fieldName)) {
            continue;
        }
        emittedFieldNames.add(fieldName);
        fieldConfigMap[fieldName] = buildDefaultFieldConfig(
            fieldName,
            IpxCredentialFieldClassification.PUBLIC,
            requiredFieldNames.has(fieldName)
        );
    }

    for (const fieldName of atomicDefinition.secretFields) {
        if (emittedFieldNames.has(fieldName)) {
            continue;
        }
        emittedFieldNames.add(fieldName);
        fieldConfigMap[fieldName] = buildDefaultFieldConfig(
            fieldName,
            IpxCredentialFieldClassification.SECRET,
            requiredFieldNames.has(fieldName)
        );
    }

    return fieldConfigMap;
}

export function listFieldDescriptorsFromConfig(
    fieldConfigMap: IpxCredentialFieldConfigMap
): IpxCredentialFieldDescriptor[] {
    const descriptors: IpxCredentialFieldDescriptor[] = [];
    for (const fieldName of Object.keys(fieldConfigMap)) {
        const fieldConfig = fieldConfigMap[fieldName];
        if (fieldConfig === undefined) {
            continue;
        }
        descriptors.push({
            field_name: fieldConfig.field_name,
            field_kind: fieldConfig.field_kind,
            field_classification: fieldConfig.field_classification,
            required: fieldConfig.required,
            display: fieldConfig.display,
            rotatable: fieldConfig.rotatable,
            ...(fieldConfig.confirmation_field_name === undefined
                ? {}
                : {
                      confirmation_field_name:
                          fieldConfig.confirmation_field_name,
                  }),
        });
    }
    return descriptors;
}

function listIpxCredentialFieldConfigNamesByClassification(
    fieldConfigMap: IpxCredentialFieldConfigMap,
    fieldClassification: IpxCredentialFieldClassification
): readonly IpxCredentialFieldName[] {
    const fieldNames: IpxCredentialFieldName[] = [];
    for (const fieldName of Object.keys(fieldConfigMap)) {
        const fieldConfig = fieldConfigMap[fieldName];
        if (fieldConfig?.field_classification === fieldClassification) {
            fieldNames.push(fieldName);
        }
    }
    return fieldNames;
}

export function listIpxCredentialFieldConfigPublicFields(
    fieldConfigMap: IpxCredentialFieldConfigMap
): readonly IpxCredentialFieldName[] {
    return listIpxCredentialFieldConfigNamesByClassification(
        fieldConfigMap,
        IpxCredentialFieldClassification.PUBLIC
    );
}

export function listIpxCredentialFieldConfigSecretFields(
    fieldConfigMap: IpxCredentialFieldConfigMap
): readonly IpxCredentialFieldName[] {
    return listIpxCredentialFieldConfigNamesByClassification(
        fieldConfigMap,
        IpxCredentialFieldClassification.SECRET
    );
}

export function listIpxCredentialFieldConfigRequiredFields(
    fieldConfigMap: IpxCredentialFieldConfigMap
): readonly IpxCredentialFieldName[] {
    return Object.values(fieldConfigMap)
        .filter((fieldConfig) => fieldConfig.required)
        .map((fieldConfig) => fieldConfig.field_name);
}

export function buildAtomicCredentialDefinitionFromFieldConfig(
    name: string,
    fieldConfigMap: IpxCredentialFieldConfigMap,
    credentialOwnerScope: CredentialOwnerScope
): AtomicCredentialTypeDefinition {
    return {
        name,
        publicFields: [
            ...listIpxCredentialFieldConfigPublicFields(fieldConfigMap),
        ],
        secretFields: [
            ...listIpxCredentialFieldConfigSecretFields(fieldConfigMap),
        ],
        requiredFields: [
            ...listIpxCredentialFieldConfigRequiredFields(fieldConfigMap),
        ],
        credentialOwnerScope,
    };
}

export interface IpxCredentialFieldDescriptor {
    field_name: string;
    field_kind: IpxCredentialFieldKind;
    field_classification: IpxCredentialFieldClassification;
    required: boolean;
    display: IpxCredentialFieldDisplayMetadata;
    rotatable?: boolean;
    confirmation_field_name?: IpxCredentialFieldName;
}

export function listIpxCredentialFieldDescriptors(
    atomicDefinition: AtomicCredentialTypeDefinition
): IpxCredentialFieldDescriptor[] {
    return listFieldDescriptorsFromConfig(
        buildDefaultFieldConfigForDefinition(atomicDefinition)
    );
}

function toCredentialFieldDisplayLabel(fieldName: string): string {
    const normalizedWords = fieldName
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .split(/[\s_-]+/)
        .map((word) => word.trim())
        .filter((word) => word.length > 0)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1));
    return normalizedWords.length === 0 ? fieldName : normalizedWords.join(' ');
}

export interface IpxCredentialSummaryCapabilities {
    can_display: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_disable: boolean;
    can_enable: boolean;
    can_restore: boolean;
    can_reveal_secret_fields: boolean;
    reveal_secret_fields_disabled_reason?: string;
    can_rotate: boolean;
    rotate_disabled_reason: CredentialRotationDisabledReason | null;
    can_view_history: boolean;
    history_disabled_reason: CredentialRotationDisabledReason | null;
}

export interface IpxCredentialOwnerMetadata {
    role_assignment_uuid: string;
    user_uuid: string;
    label: string;
    role: Role;
    role_assignment_status: Status;
}

export interface IpxCredentialSummaryListItem {
    credential_uuid: string;
    organization_ipx_credential_policy_uuid: string;
    ipx_credential_definition_uuid: string;
    scope: IpxCredentialScope;
    owner?: IpxCredentialOwnerMetadata | null;
    account_id: string;
    status: Status;
    public_fields: Record<string, string>;
    masked_preview: string;
    field_descriptors: IpxCredentialFieldDescriptor[];
    capabilities: IpxCredentialSummaryCapabilities;
    rotation_eligibility: PortalCredentialRotationEligibility | null;
}

export interface IpxCredentialPolicyCredentialCollectionCapabilities {
    can_create_organization_credential: boolean;
    can_create_role_assignment_credential: boolean;
}

export interface ListIpxCredentialsForPolicyResponse {
    policy: OrganizationIpxCredentialPolicyListItem;
    capabilities: IpxCredentialPolicyCredentialCollectionCapabilities;
    field_descriptors: IpxCredentialFieldDescriptor[];
    credentials: IpxCredentialSummaryListItem[];
    eligible_missing_role_assignments: EligibleIpxCredentialRoleAssignmentListItem[];
}

export type IpxCredentialEditedFields = Record<string, string>;

export interface IpxCredentialSaveFields {
    account_id: string;
    edited_fields: IpxCredentialEditedFields;
}

export interface CreateIpxCredentialRequest extends IpxCredentialSaveFields {
    scope: IpxCredentialScope;
}

export interface CreateDashboardWebsiteCredentialRequest
    extends IpxCredentialSaveFields {
    organization_uuid: string;
    organization_ipx_credential_policy_uuid: string;
}

export interface UpdateIpxCredentialRequest extends IpxCredentialSaveFields {
    credential_uuid: string;
}

export interface RevealIpxCredentialSecretFieldRequest {
    credential_uuid: string;
    field_name: string;
}

export interface RevealIpxCredentialSecretFieldResponse {
    field_name: string;
    field_value: string;
}

export interface EligibleIpxCredentialRoleAssignmentListItem {
    role_assignment_uuid: string;
    user_uuid: string;
    label: string;
    role: Role;
    role_assignment_status: Status;
    create_capability: IpxCredentialCreateCapability;
}

export interface IpxCredentialCreateCapability {
    can_create: boolean;
    disabled_reason?: string;
}

export interface IpxCredentialRuntimeRoleScope {
    organization_uuid: string;
    role_assignment_uuid: string;
}

export interface IpxCredentialMaskedCredentialMetadata {
    credential_uuid: string;
    credential_owner_scope: CredentialOwnerScope;
    account_id: string;
    masked_preview: string;
    status: Status;
}

export interface IpxCredentialRuntimeWorkflowProjection {
    role_scope: IpxCredentialRuntimeRoleScope;
    definition: IpxWebLoginCredentialDefinitionProjection;
    credential: IpxCredentialMaskedCredentialMetadata | null;
    revision_hash: string;
}

export interface IpxCredentialRuntimeBootstrapRequest {
    role_scope: IpxCredentialRuntimeRoleScope;
}

export interface IpxCredentialRuntimeBootstrapResponse {
    role_scope: IpxCredentialRuntimeRoleScope;
    workflow_projections: IpxCredentialRuntimeWorkflowProjection[];
}

export interface IpxCredentialDefinitionIdentity {
    ipx_credential_definition_uuid: string;
    system_id: SupportedCredentialSystemId;
    credential_definition_key: string;
}

export interface IpxCredentialRuntimeMatchedUrlData {
    page_url: string;
    matched_url: string;
    matched_url_pattern: UrlPatternString;
}

export interface IpxCredentialRuntimeResolveRequest {
    role_scope: IpxCredentialRuntimeRoleScope;
    definition: IpxCredentialDefinitionIdentity;
    revision_hash: string;
    matched_url: IpxCredentialRuntimeMatchedUrlData;
    credential_uuid?: string;
}

export const IpxCredentialRuntimeResolveResult = {
    RESOLVED: 'resolved',
    MISSING_CREDENTIAL: 'missing_credential',
    STALE_PROJECTION: 'stale_projection',
} as const;
export type IpxCredentialRuntimeResolveResult =
    (typeof IpxCredentialRuntimeResolveResult)[keyof typeof IpxCredentialRuntimeResolveResult];

export interface IpxCredentialRuntimeResolvedResponse {
    result: typeof IpxCredentialRuntimeResolveResult.RESOLVED;
    credential_uuid: string;
    revision_hash: string;
    field_values: Record<string, string>;
}

export interface IpxCredentialRuntimeMissingCredentialResponse {
    result: typeof IpxCredentialRuntimeResolveResult.MISSING_CREDENTIAL;
    revision_hash: string;
}

export interface IpxCredentialRuntimeStaleProjectionResponse {
    result: typeof IpxCredentialRuntimeResolveResult.STALE_PROJECTION;
    current_revision_hash: string;
}

export type IpxCredentialRuntimeResolveResponse =
    | IpxCredentialRuntimeResolvedResponse
    | IpxCredentialRuntimeMissingCredentialResponse
    | IpxCredentialRuntimeStaleProjectionResponse;

export interface RpaBuilderActiveIpxContext {
    page_url: string;
    system_id: SupportedCredentialSystemId | null;
    matched_url_patterns: UrlPatternString[];
}

export interface RpaBuilderCapabilityFlags {
    can_use_rpa_builder: boolean;
    can_manage_credential_definitions: boolean;
    can_configure_credentials: boolean;
}

export interface RpaBuilderBootstrapRequest {
    active_ipx_context: RpaBuilderActiveIpxContext;
}

export interface RpaBuilderBootstrapResponse {
    active_ipx_context: RpaBuilderActiveIpxContext;
    definitions: RpaBuilderCredentialDefinitionProjection[];
    capabilities: RpaBuilderCapabilityFlags;
}

export interface RpaBuilderListCredentialDefinitionsRequest {
    system_id?: SupportedCredentialSystemId;
    credential_definition_key?: string;
    status?: Status;
}

export interface RpaBuilderCredentialDefinitionSharedMetadata {
    active_policy_count: number;
    has_active_policy_outside_active_organization: boolean;
}

export interface RpaBuilderCredentialDefinitionProjection
    extends Omit<
        IpxWebLoginCredentialDefinitionProjection,
        'ipx_web_resource_model'
    > {
    revision_hash: string;
    ipx_web_resource_model?: RpaBuilderWebResourceModelProjection | null;
}

export interface RpaBuilderCredentialDefinitionListItem extends RpaBuilderCredentialDefinitionProjection {}

export interface RpaBuilderListCredentialDefinitionsResponse {
    definitions: RpaBuilderCredentialDefinitionListItem[];
}

export interface RpaBuilderSaveCredentialDefinitionFields {
    system_id: SupportedCredentialSystemId;
    credential_definition_key: string;
    ipx_web_resource_model_uuid?: string;
    trigger_url_patterns: UrlPatternString[];
    credential_field_config: IpxCredentialFieldConfigMap;
    login_workflow: IpxLoginWorkflow;
}

export interface RpaBuilderCreateCredentialDefinitionRequest extends RpaBuilderSaveCredentialDefinitionFields {}

export interface RpaBuilderUpdateCredentialDefinitionRequest extends RpaBuilderSaveCredentialDefinitionFields {
    ipx_credential_definition_uuid: string;
    expected_revision_hash: string;
}

export interface RpaBuilderConfigureLoginWorkflowCredentialDefinitionCreateRequest
    extends RpaBuilderSaveCredentialDefinitionFields {
    operation: 'create';
}

export interface RpaBuilderConfigureLoginWorkflowCredentialDefinitionUpdateRequest
    extends RpaBuilderUpdateCredentialDefinitionRequest {
    operation: 'update';
}

export type RpaBuilderConfigureLoginWorkflowCredentialDefinitionRequest =
    | RpaBuilderConfigureLoginWorkflowCredentialDefinitionCreateRequest
    | RpaBuilderConfigureLoginWorkflowCredentialDefinitionUpdateRequest;

export interface RpaBuilderSaveCredentialDefinitionResponse {
    definition: IpxWebLoginCredentialDefinitionProjection;
    revision_hash: string;
}

export interface RpaBuilderDeleteCredentialDefinitionRequest {
    ipx_credential_definition_uuid: string;
}

export interface RpaBuilderDeleteCredentialDefinitionResponse {
    definition: IpxWebLoginCredentialDefinitionProjection;
}

export interface RpaBuilderCredentialPolicyMutationRequest {
    role_scope: IpxCredentialRuntimeRoleScope;
    ipx_credential_definition_uuid: string;
}

export interface RpaBuilderAttachCredentialPolicyRequest extends RpaBuilderCredentialPolicyMutationRequest {}

export interface RpaBuilderReactivateCredentialPolicyRequest extends RpaBuilderCredentialPolicyMutationRequest {}

export interface RpaBuilderDeactivateCredentialPolicyRequest extends RpaBuilderCredentialPolicyMutationRequest {}

export interface RpaBuilderCredentialPolicyMutationResponse {
    definition: IpxWebLoginCredentialDefinitionProjection;
    revision_hash: string;
}

export interface RpaBuilderAttachCredentialPolicyResponse extends RpaBuilderCredentialPolicyMutationResponse {}

export interface RpaBuilderReactivateCredentialPolicyResponse extends RpaBuilderCredentialPolicyMutationResponse {}

export interface RpaBuilderDeactivateCredentialPolicyResponse extends RpaBuilderCredentialPolicyMutationResponse {}

export interface RpaBuilderWebResourceModelIdentity {
    ipx_web_resource_model_uuid: string;
}

export interface RpaBuilderValidWebResourceModelProjection
    extends RpaBuilderWebResourceModelIdentity, IpxCredentialRuntimeWebResourceModelProjection {
    system_id: SupportedCredentialSystemId;
    initial_navigation_url: string;
    status: Status;
}

export const RpaBuilderInvalidWebResourceModelReason = {
    INVALID_MODEL: 'invalid_model',
    UNSUPPORTED_LEGACY_MODEL: 'unsupported_legacy_model',
} as const;
export type RpaBuilderInvalidWebResourceModelReason =
    (typeof RpaBuilderInvalidWebResourceModelReason)[keyof typeof RpaBuilderInvalidWebResourceModelReason];

export interface RpaBuilderInvalidWebResourceModelMetadata {
    reason: RpaBuilderInvalidWebResourceModelReason;
    error_message: string;
}

export interface RpaBuilderInvalidWebResourceModelProjection
    extends RpaBuilderWebResourceModelIdentity {
    system_id: SupportedCredentialSystemId;
    initial_navigation_url: string;
    status: Status;
    invalid_web_resource_model: RpaBuilderInvalidWebResourceModelMetadata;
}

export type RpaBuilderWebResourceModelProjection =
    | RpaBuilderValidWebResourceModelProjection
    | RpaBuilderInvalidWebResourceModelProjection;

export interface RpaBuilderListWebResourceModelsRequest {
    system_id?: SupportedCredentialSystemId;
    initial_navigation_url?: string;
    status?: Status;
}

export interface RpaBuilderListWebResourceModelsResponse {
    models: RpaBuilderWebResourceModelProjection[];
}

export interface RpaBuilderFetchWebResourceModelRequest extends RpaBuilderWebResourceModelIdentity {}

export interface RpaBuilderFetchWebResourceModelResponse {
    model: RpaBuilderWebResourceModelProjection;
}

export interface RpaBuilderDeleteWebResourceModelRequest extends RpaBuilderWebResourceModelIdentity {}

export interface RpaBuilderDeleteWebResourceModelResponse {
    model: RpaBuilderWebResourceModelProjection;
}

export interface RpaBuilderSaveWebResourceModelFields {
    system_id: SupportedCredentialSystemId;
    initial_navigation_url: string;
    web_resource_model: IpxWebResourceModelDocument;
}

export interface RpaBuilderCreateWebResourceModelRequest extends RpaBuilderSaveWebResourceModelFields {}

export interface RpaBuilderReplaceWebResourceModelRequest
    extends RpaBuilderWebResourceModelIdentity, RpaBuilderSaveWebResourceModelFields {}

export interface RpaBuilderConfigureLoginWorkflowWebResourceModelCreateRequest
    extends RpaBuilderCreateWebResourceModelRequest {
    operation: 'create';
}

export interface RpaBuilderConfigureLoginWorkflowWebResourceModelReplaceRequest
    extends RpaBuilderReplaceWebResourceModelRequest {
    operation: 'replace';
    expected_model_digest: string;
}

export type RpaBuilderConfigureLoginWorkflowWebResourceModelRequest =
    | RpaBuilderConfigureLoginWorkflowWebResourceModelCreateRequest
    | RpaBuilderConfigureLoginWorkflowWebResourceModelReplaceRequest;

export interface RpaBuilderConfigureLoginWorkflowSaveRequest {
    credential_definition: RpaBuilderConfigureLoginWorkflowCredentialDefinitionRequest;
    web_resource_model?: RpaBuilderConfigureLoginWorkflowWebResourceModelRequest;
}

export interface RpaBuilderConfigureLoginWorkflowSaveResponse {
    credential_definition: RpaBuilderCredentialDefinitionProjection;
    web_resource_model: RpaBuilderWebResourceModelProjection | null;
}

export interface RpaBuilderSaveWebResourceModelResponse {
    model: RpaBuilderWebResourceModelProjection;
}

/**
 * Organization secret (returned from API)
 */
export interface Credentials {
    credential_id: number;
    credential_uuid: string;
    organization_id: number;
    organization_uuid: string;
    system_id: SupportedCredentialSystemId;
    account_id: string; // Unique mnemonic id assigned by the client
    credential_type: CredentialType;
    public_fields: Record<string, unknown>; // Cleartext searchable fields
    masked_preview: string; // Masked credential preview for UI
    status: Status;
    applies_to_children: boolean; // Applies to descendant orgs
    ipx_credential_policy_link?: SecretIpxCredentialPolicyLinkMetadata | null;
}

/**
 * Organization secret with decrypted credentials (requires DISPLAY permission)
 */
export interface CredentialsWithCredentials extends Credentials {
    credentials: ThirdPartyCredentialsDraft; // Decrypted credentials may be incomplete drafts
}

export interface SecretUuidReference {
    credential_uuid: string;
}

export interface SecretOrganizationUuidReference {
    organization_uuid: string;
}

/**
 * List secrets response
 */
export interface SecretListItem extends SecretMetadataResponse {
    capabilities: SecretListItemCapabilities;
    usage_data: SecretListItemUsageData;
}

export interface ListSecretsResponse {
    secrets: SecretListItem[];
    pagination?: OpaqueCursorPagination;
    capabilities: ListSecretsResponseCapabilities;
}

export interface SecretCredentialDefinitionListItem {
    ipx_credential_definition_uuid: string;
    system_id: SupportedCredentialSystemId;
    credential_type: CredentialType;
    name: string;
    credential_definition_key: string;
    credential_owner_scope: CredentialOwnerScope;
    status: Status;
    field_descriptors: IpxCredentialFieldDescriptor[];
}

export interface ListSecretCredentialDefinitionsQuery {
    organization_uuid: string;
    system_ids?: SupportedCredentialSystemId[];
    credential_types?: CredentialType[];
}

export interface ListSecretCredentialDefinitionsResponse {
    definitions: SecretCredentialDefinitionListItem[];
}

export interface SecretListItemCapabilities {
    can_display: boolean;
    can_edit: boolean;
    can_delete: boolean;
}

export interface ListSecretsResponseCapabilities {
    can_create: boolean;
    can_show_deleted: boolean;
}

export interface SecretListItemUsageSummary {
    logical_terminal_count: number;
    terminal_device_count: number;
    total_count: number;
    has_usages: boolean;
}

export interface SecretListItemUsageData {
    summary: SecretListItemUsageSummary;
    usages: CredentialUsageListItem[];
}

export interface ListSecretsForOrganizationQuery {
    actions?: AuthorizableAction[];
    credential_type?: CredentialType;
    terms?: string[];
    include_descendants?: boolean;
    show_deleted?: boolean;
    system_ids?: SupportedCredentialSystemId[];
    exclude_policy_linked?: boolean;
    limit?: number;
    cursor?: string;
}

export const CredentialUsageType = {
    LOGICAL_TERMINAL: 'logical_terminal',
    TERMINAL_DEVICE: 'terminal_device',
} as const;

export type CredentialUsageType = (typeof CredentialUsageType)[keyof typeof CredentialUsageType];

export interface LogicalTerminalUsageReference {
    usage_type: typeof CredentialUsageType.LOGICAL_TERMINAL;
    logical_terminal_uuid: string;
}

export interface TerminalDeviceUsageReference {
    usage_type: typeof CredentialUsageType.TERMINAL_DEVICE;
    terminal_device_uuid: string;
}

export type CredentialUsageReference = LogicalTerminalUsageReference | TerminalDeviceUsageReference;

export type LogicalTerminalUsageListItem = LogicalTerminalUsageReference & {
    display_name: string;
};

export type TerminalDeviceUsageListItem = TerminalDeviceUsageReference & {
    display_name: string;
};

export type CredentialUsageListItem = LogicalTerminalUsageListItem | TerminalDeviceUsageListItem;

export interface CredentialUsagesResponse {
    usages: CredentialUsageListItem[];
}

export interface CredentialDeleteConflictResponse {
    message: string;
    usages: CredentialUsageListItem[];
}

export interface SecretIdentityReference {
    credential_id: number;
    credential_uuid: string;
}

export interface SecretOrganizationReference {
    organization_id: number;
    organization_uuid: string;
}

export interface DisconnectCredentialUsagesRequest {
    usages: CredentialUsageReference[];
}

export interface DisconnectCredentialUsagesResponse {
    disconnected_count: number;
    remaining_usage_count: number;
    message: string;
}

export interface IpxCredentialRequestFields {
    credential_owner_scope: CredentialOwnerScope;
    ipx_credential_definition_uuid: string;
}

/**
 * Create secret request
 */
export interface CreateSecretRequest {
    organization_uuid: string;
    system_id: SupportedCredentialSystemId;
    account_id: string;
    credential_type: CredentialType;
    credentials: ThirdPartyCredentialsDraft;
    credential_label?: string;
    applies_to_children?: boolean;
    credential_owner_scope?: CredentialOwnerScope;
    ipx_credential_definition_uuid?: string;
}

/**
 * Update secret request
 */
export interface UpdateSecretRequest {
    account_id?: string;
    credential_type?: CredentialType;
    credentials?: ThirdPartyCredentialsDraft;
    credential_label?: string;
    applies_to_children?: boolean;
    credential_owner_scope?: CredentialOwnerScope;
    ipx_credential_definition_uuid?: string;
    status?: Status;
}

/**
 * Get secret response (with decrypted credentials)
 */
export interface GetSecretResponse extends SecretWithCredentialsResponse {}

export interface SecretWithCredentialsResponse extends CreateSecretResponse {
    credentials: ThirdPartyCredentialsDraft;
}

/**
 * Create secret response
 */
export interface CreateSecretResponse {
    credential_uuid: string;
    organization_uuid: string;
    system_id: SupportedCredentialSystemId;
    account_id: string;
    credential_type: CredentialType;
    public_fields: Record<string, unknown>;
    masked_preview: string;
    status: Status;
    applies_to_children: boolean;
    ipx_credential_policy_link?: SecretIpxCredentialPolicyLinkMetadata | null;
}

export interface SecretMetadataResponse extends CreateSecretResponse {}

export interface SecretIpxCredentialPolicyLinkMetadata {
    organization_ipx_credential_policy_uuid: string;
    ipx_credential_definition_uuid: string;
    policy_status: Status;
}

/**
 * Update secret response
 */
export interface SaveSecretResponse extends CreateSecretResponse {}
